import smLogo from "@assets/images/smo-blue.svg";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  TextField,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import {
  passwordResetUrl,
  userLoginPath,
  userSignupPath
} from "@saleor/auth/urls";
import { useSavedAccount } from "@saleor/components/AppLayout/SavedAccountContext";
import Form from "@saleor/components/Form/Form";
import useNavigator from "@saleor/hooks/useNavigator";
import useUser from "@saleor/hooks/useUser";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import useStyles from "./styles";

interface FormData {
  email: string;
  password: string;
}

const LoginSavedAccount: React.FC = () => {
  const classes = useStyles({});
  const navigate = useNavigator();
  const { setRemoveAccount, savedAccounts, recentUser } = useSavedAccount();
  const [selectedLogin, setSelectedLogin] = useState(null);

  const handleLoginNavigation = () => {
    navigate(userLoginPath);
  };

  const handleSelectUser = (email: string) => {
    const availableUsers = [...[recentUser], ...savedAccounts];

    const selectedUser = availableUsers.find(
      availableUser => availableUser.email === email
    );
    if (selectedUser) {
      setSelectedLogin(selectedUser);
    } else {
      setSelectedLogin(null);
    }
  };

  return (
    <div className={classes.loginWrapper}>
      <img className={classes.logo} src={smLogo} />

      <Card className={classes.loginCard}>
        <CardContent>
          <Typography component="h1" className={classes.header}>
            Choose an Account
          </Typography>
          <Box>
            <List
              className={classes.accountsList}
              disablePadding
              subheader={
                <ListSubheader className={classes.subHeader} disableGutters>
                  SAVED ACCOUNTS
                </ListSubheader>
              }
            >
              {savedAccounts.map((savedAccount, index) => (
                <SavedAccountItem
                  handleSelectUser={handleSelectUser}
                  key={index}
                  savedAccount={savedAccount}
                  setRemoveAccount={setRemoveAccount}
                  recentUser={recentUser}
                />
              ))}
            </List>
            <Button
              onClick={handleLoginNavigation}
              className={classes.loginExisting}
              startIcon={<PersonAddIcon />}
              variant="text"
            >
              Login an existing account
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box className={classes.signUpWrapper}>
        <Typography component="span">
          Interested in being an online seller?{" "}
        </Typography>

        <Link to={userSignupPath} className={classes.signUpLink}>
          <Typography component="span" className={classes.signUpLabel}>
            Sign Up here!
          </Typography>
        </Link>
      </Box>
      {!!selectedLogin ? (
        <LoginFormDialog
          selectedLogin={selectedLogin}
          setSelectedLogin={setSelectedLogin}
        />
      ) : null}
    </div>
  );
};

const SavedAccountItem = ({
  handleSelectUser,
  savedAccount,
  setRemoveAccount,
  recentUser
}) => {
  const classes = useStyles({});

  return (
    <ListItem
      button
      disableGutters
      classes={{
        button: classes.savedAccountContent,
        container: classes.savedAccountItemContainer,
        root: classes.savedAccountItem
      }}
      onClick={() => handleSelectUser(savedAccount.email)}
    >
      <ListItemText
        className={classes.accountName}
        primary={`${savedAccount.firstName} ${savedAccount.lastName}`}
        secondary={savedAccount.email}
      ></ListItemText>
      <ListItemSecondaryAction className={classes.savedAccountItemButton}>
        {!!recentUser && recentUser.email === savedAccount.email ? (
          <Typography className={classes.signedOutText} component="span">
            Signed Out
          </Typography>
        ) : null}
        <ListItemIcon>
          <IconButton
            onClick={() => setRemoveAccount(savedAccount.email)}
            className={classes.trashButton}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </ListItemIcon>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

const LoginFormDialog = ({ selectedLogin, setSelectedLogin }) => {
  const { login, userDetail, tokenAuthLoading } = useUser();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const classes = useStyles({});
  const handleClose = () => {
    setSelectedLogin(null);
    setHasError(false);
    setIsSubmitted(false);
  };

  useEffect(() => {
    if (
      isSubmitted &&
      !tokenAuthLoading &&
      (userDetail === null || userDetail === undefined)
    ) {
      setHasError(true);
    } else {
      setHasError(false);
    }

    return () => {
      setHasError(false);
    };
  }, [tokenAuthLoading]);

  const onSubmit = ({ email, password }: FormData) => {
    setIsSubmitted(true);
    if (!password) {
      setHasError(true);
      return;
    }

    login(email, password);
  };

  const initialForm = {
    email: selectedLogin.email,
    password: ""
  };

  return (
    <Dialog
      open={!!selectedLogin}
      classes={{
        paper: classes.dialog
      }}
      fullWidth
    >
      <Form initial={initialForm} onSubmit={onSubmit}>
        {({ change: handleChange, submit: handleSubmit }) => (
          <>
            <DialogHeader handleClose={handleClose} />
            <DialogContent className={classes.dialogContent}>
              <Typography component="p" className={classes.dialogWelcome}>
                Welcome Back!
              </Typography>
              <Typography
                component="p"
                className={classes.dialogSelectedAccount}
              >
                {selectedLogin.firstName} {selectedLogin.lastName}
              </Typography>

              <TextField
                label="Password"
                variant="outlined"
                error={!tokenAuthLoading && hasError}
                helperText={
                  !tokenAuthLoading && hasError
                    ? "Sorry, password is incorrect. Please try again."
                    : null
                }
                fullWidth
                onChange={handleChange}
                className={classes.dialogPassword}
                name="password"
                type="password"
                autoFocus
              />
            </DialogContent>
            <DialogFooter
              handleSubmit={handleSubmit}
              isLoading={tokenAuthLoading}
            />
          </>
        )}
      </Form>
    </Dialog>
  );
};

interface DialogHeaderProps {
  handleClose: () => void;
}

const DialogHeader: React.FC<DialogHeaderProps> = ({ handleClose }) => {
  const classes = useStyles({});

  return (
    <DialogTitle className={classes.dialogTitle}>
      <IconButton className={classes.dialogCloseButton} onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography className={classes.dialogTitleText} component="div">
        Login
      </Typography>
    </DialogTitle>
  );
};

interface DialogFooterProps {
  handleSubmit: () => void;
  isLoading: boolean;
}
const DialogFooter: React.FC<DialogFooterProps> = ({
  handleSubmit,
  isLoading
}) => {
  const classes = useStyles({});
  const navigate = useNavigator();
  const handleForgotPassword = () => {
    navigate(passwordResetUrl);
  };

  return (
    <DialogActions className={classes.dialogActions}>
      <Typography
        className={classes.dialogForgotPassword}
        onClick={handleForgotPassword}
      >
        Forgot your password?
      </Typography>
      <Button
        disabled={isLoading}
        id="btnLogin"
        className={classes.dialogLoginButton}
        variant="contained"
        onClick={handleSubmit}
      >
        Login
      </Button>
    </DialogActions>
  );
};

LoginSavedAccount.displayName = "LoginSavedAccount";
export default LoginSavedAccount;
